import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyIcon, CheckIcon } from 'lucide-react';
import { cn, toast } from '@noor/lib';

interface CoordinatesRowProps {
  latitude: number;
  longitude: number;
  /**
   * Keep the copy glyph visible instead of revealing it on hover. Set on
   * MOBILE: touch has no hover state, so a hover-only icon would leave the
   * affordance invisible there — same rule as `OrderNumber`.
   */
  alwaysShowIcon?: boolean | undefined;
  className?: string | undefined;
}

/**
 * The picked point, under the address field, as a copy-to-clipboard button.
 *
 * The address input is read-only — the map picker owns it — so the coordinates
 * it wrote are otherwise invisible. Surfacing them here is what lets a merchant
 * check the branch landed where they meant, and hand the pair to a courier or
 * a support ticket without reopening the picker.
 *
 * Renders NOTHING until a point is picked. `0, 0` is the form's "never picked"
 * sentinel (see the schema's `superRefine`), so printing it would show a
 * plausible-looking Null Island fix for a branch that has no location at all.
 *
 * Copies as `41.311081, 69.240562` — comma-separated, latitude first, which is
 * what Google/Yandex Maps and every geocoder accept when pasted straight in.
 * Six decimals is ~0.1 m, past which the digits are noise.
 */
export function CoordinatesRow({
  latitude,
  longitude,
  alwaysShowIcon,
  className,
}: CoordinatesRowProps) {
  const { t } = useTranslation('eats');
  const [copied, setCopied] = useState(false);

  const isPlaced = latitude !== 0 || longitude !== 0;
  if (!isPlaced) return null;

  const text = `${format(latitude)}, ${format(longitude)}`;

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // A denied clipboard permission (or an insecure origin) is the only way
      // this fails; there is nothing to retry, so say nothing rather than
      // claiming a copy that did not happen.
      return;
    }
    setCopied(true);
    toast.success(t('branches.form.coordinatesCopied'));
    // Purely cosmetic — the timer is not cleared on unmount because setting
    // state on an unmounted component is a no-op in React 18+.
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      type="button"
      // `group` so the icon reacts to the BUTTON's hover, not each child's.
      onClick={() => void copy()}
      title={t('branches.form.coordinatesCopy')}
      aria-label={t('branches.form.coordinatesCopyLabel', {
        latitude: format(latitude),
        longitude: format(longitude),
      })}
      className={cn(
        'group flex w-fit min-w-0 cursor-pointer items-center gap-1.5 px-1 text-left',
        'text-xs tracking-tight text-muted-foreground tabular-nums',
        'rounded-sm transition-colors outline-none hover:text-foreground',
        'focus-visible:ring-[3px] focus-visible:ring-ring/40',
        className,
      )}
    >
      <span className="truncate">{text}</span>
      {copied ? (
        <CheckIcon className="size-3.5 shrink-0 text-primary" aria-hidden />
      ) : (
        <CopyIcon
          className={cn(
            'size-3.5 shrink-0 transition-opacity',
            !alwaysShowIcon &&
              'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
          )}
          aria-hidden
        />
      )}
    </button>
  );
}

/**
 * Six decimals (~0.1 m) without trailing-zero padding, so a round point reads
 * `41.3, 69.24` rather than `41.300000, 69.240000`. `Number.parseFloat` on the
 * fixed string is what drops the padding while keeping the rounding.
 */
function format(value: number): string {
  return String(Number.parseFloat(value.toFixed(6)));
}
